import React, { FC, useState, useCallback, useEffect } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import {
  DndContext,
  closestCenter,
  MouseSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';

// eslint-disable-next-line import/no-extraneous-dependencies
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import Grid from 'components/layouts/Grid';
import SortableItem from 'components/grid/SortableItem';
import PageLayout from 'components/layouts/PageLayout';
import { useWindowSize } from 'hooks/useWindowSize';
import ServiceHelper from 'stores/ServiceHelper';
import { useParams } from 'react-router-dom';
import { AdminListingImageDto } from 'services/data-contracts';
import DropZone from 'components/DropZone';
import { useStoreWrapper } from 'stores/StoreHelper';
import ImageDetail from './photoPage/ImageDetail';
import { useStateContext } from 'providers/usePageState';
import styled from 'styled-components';
import { Checkbox } from '@mui/material';
import { Button } from 'saladsoft-primitive';
import { toast } from 'react-toastify';

interface AdminListingImageModel extends AdminListingImageDto {
  isChecked: boolean;
  progress?: number;
}

const PhotoPage = () => {
  const { listingId } = useParams();
  const { uiStore } = useStoreWrapper();
  const { broadcast } = useStateContext();

  const [items, setItems] = useState<AdminListingImageModel[]>([]);
  const [hasChecked, setHasChecked] = useState(false);

  const { width } = useWindowSize();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      onActivation: (event) => console.log('onActivation', event),
      activationConstraint: { distance: 5 },
    }),
  );

  const getColumnCount = useCallback(() => {
    const baseColumnCount = 5;
    if (width < 1800) {
      const maxItemWidth = 300;
      const calculatedColumns = Math.floor(width / maxItemWidth);
      return Math.max(calculatedColumns, 1); // 최소 1개의 열은 유지
    }
    return baseColumnCount;
  }, [width]);

  const getImages = async () => {
    if (!listingId) return;
    const result = (await ServiceHelper?.adminListingService?.getListingImages(Number(listingId), {
      size: 1000,
      sort: ['seq,asc'],
    })) as any;
    setItems(result?.data ?? []);
  };

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === Number(active.id));
        const newIndex = items.findIndex((item) => item.id === Number(over!.id));
        const target = arrayMove(items, oldIndex, newIndex);
        const reorder = target.map((item) => item.id);

        ServiceHelper?.adminListingService?.reorderListingImages(
          Number(listingId),
          //항상 넘버 배열로 보내기(손대지 말것)
          reorder as number[],
        );
        return target;
      });
    }
  }, []);

  const handleDragCancel = useCallback(() => {}, []);

  const handleClickImage = (item: AdminListingImageDto) => {
    uiStore?.modal.show({
      style: { width: 900, height: '100%', maxHeight: '80vh' },
      children: <ImageDetail listingId={Number(listingId)} image={item} />,
      onConfirmed: () => {
        getImages();
      },
    });
  };

  const handleDeleteChecked = () => {
    uiStore?.confirm.show({
      title: '사진 삭제',
      message: '선택한 사진을 삭제하시겠습니까?',
      onConfirmed: async () => {
        const target = items.filter((item) => item.isChecked);

        try {
          // 선택된 모든 이미지 삭제
          await Promise.all(
            target.map((item) =>
              ServiceHelper?.adminListingService?.deleteListingImage(
                Number(listingId),
                item.id as number,
              ),
            ),
          );

          // 삭제 후 이미지 목록 새로고침
          await getImages();

          // 완료 알림
          toast.success('사진 삭제가 완료되었습니다.');
        } catch (error) {
          toast.error('사진 삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  const handleDrop = useCallback(
    async (files: File[]) => {
      const uploadPromises = files.map(async (file) => {
        const tempId = -(Date.now() + Math.random() * 1000); // 고유한 임시 ID 생성
        const tempImageUrl = URL.createObjectURL(file);

        setItems((prev) => [
          ...prev,
          {
            id: tempId,
            url: tempImageUrl,
            isPlan: false,
            isChecked: false,
            seq: items.length,
            progress: 0,
          } as AdminListingImageModel,
        ]);

        try {
          const result = await ServiceHelper?.adminListingService?.createListingImage(
            Number(listingId),
            { isPlan: false, seq: items.length },
            { file },
            {
              onUploadProgress: (progressEvent: any) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setItems((prev) =>
                  prev.map((item) =>
                    item.id === tempId ? { ...item, progress: Math.min(progress, 99) } : item,
                  ),
                );
              },
            },
          );

          if (result?.data) {
            setItems((prev) =>
              prev.map((item) => (item.id === tempId ? { ...item, progress: 100 } : item)),
            );

            setTimeout(() => {
              setItems((prev) =>
                prev.map((item) =>
                  item.id === tempId ? { ...result.data, isChecked: false } : item,
                ),
              );
              URL.revokeObjectURL(tempImageUrl);
            }, 300);
          }

          return result;
        } catch (error) {
          setItems((prev) => prev.filter((item) => item.id !== tempId));
          URL.revokeObjectURL(tempImageUrl);
          throw error;
        }
      });

      try {
        await Promise.all(uploadPromises);
        broadcast({ id: 'reload', param: 'photos' });
      } catch (error) {
        toast.error('일부 이미지 업로드에 실패했습니다.');
      }
    },
    [listingId, items.length],
  );

  useEffect(() => {
    const checkedItems = items.filter((item) => item.isChecked);
    setHasChecked(checkedItems.length > 0);
  }, [items]);

  useEffect(() => {
    getImages();
  }, []);

  return (
    <PageLayout title="사진관리">
      <DropZone onDrop={handleDrop} />
      <>
        <div style={{ width: 200, paddingBottom: 20 }}>
          <Button isValid={hasChecked} type="outlined" onClick={handleDeleteChecked}>
            선택 삭제
          </Button>
        </div>
      </>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        // onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={items.map((item) => ({ id: item.id?.toString() ?? '' }))}
          strategy={rectSortingStrategy}
        >
          <Grid
            columns={getColumnCount()}
            style={{ justifyContent: items.length < 5 ? 'flex-start' : 'center' }}
          >
            {items?.map((item) => (
              <SortableItem key={item.id as number} id={item.id?.toString() ?? ''}>
                {item.isPlan && <PlanWrapper>도면</PlanWrapper>}
                {(item.progress === undefined || item.progress === 100) && (
                  <CheckBoxWrapper>
                    <Checkbox
                      sx={{ background: 'white' }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        setItems((prev) =>
                          prev.map((prevItem) =>
                            prevItem.id === item.id
                              ? { ...prevItem, isChecked: e.target.checked }
                              : prevItem,
                          ),
                        );
                      }}
                    />
                  </CheckBoxWrapper>
                )}
                <img
                  onClick={() => {
                    if (item.progress === undefined || item.progress === 100) {
                      handleClickImage(item);
                    }
                  }}
                  src={item.url}
                  alt={`Image ${item.id}`}
                  style={{
                    maxWidth: '100%',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'relative',
                    zIndex: 1,
                    cursor:
                      item.progress === undefined || item.progress === 100 ? 'pointer' : 'default',
                  }}
                />
                {item.progress !== undefined && item.progress < 100 && (
                  <ProgressWrapper>
                    <ProgressBar width={item.progress} />
                  </ProgressWrapper>
                )}
              </SortableItem>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
    </PageLayout>
  );
};
const PlanWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px;
  border-radius: 5px;
`;
const CheckBoxWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 13;
  background-color: white;
  border-radius: 55px;
`;

const ProgressWrapper = styled.div`
  position: absolute;
  bottom: 5px;
  left: 5px;
  right: 5px;
  height: 8px;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 2;
  border: 2px solid white;
`;

const ProgressBar = styled.div<{ width: number }>`
  height: 100%;
  background-color: #2196f3;
  transition: width 0.3s ease;
  width: ${(props) => props.width}%;
`;

export default PhotoPage;
